import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/* Featured "Inside the Book" gallery — one large image at a time, shown whole
   (object-contain, no cropping), with a peek of the next slide, dot indicators,
   desktop gold arrows and a fading mobile swipe hint. */
export default function ImageGallery({
  images = [],
  label = "Inside the book",
  testId = "book-gallery",
  idPrefix = "gallery",
  alt = "Inside the book",
}) {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);
  const [interacted, setInteracted] = useState(false);
  const [lightbox, setLightbox] = useState(null); // index or null
  const touchX = useRef(null);

  const openLightbox = (i) => setLightbox(i);
  const closeLightbox = () => setLightbox(null);
  const lbGo = (dir) =>
    setLightbox((i) => (i === null ? i : Math.max(0, Math.min(images.length - 1, i + dir))));

  useEffect(() => {
    if (lightbox === null) return;
    const sbw = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowRight") lbGo(1);
      else if (e.key === "ArrowLeft") lbGo(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, images.length]);

  const slideStep = () => {
    const rail = railRef.current;
    const first = rail?.children?.[0];
    return first ? first.getBoundingClientRect().width + 14 : rail.clientWidth;
  };

  const onScroll = () => {
    const rail = railRef.current;
    if (!rail) return;
    if (!interacted) setInteracted(true);
    setActive(Math.round(rail.scrollLeft / slideStep()));
  };

  const go = (i) => {
    const rail = railRef.current;
    if (!rail) return;
    const idx = Math.max(0, Math.min(images.length - 1, i));
    rail.scrollTo({ left: idx * slideStep(), behavior: "smooth" });
  };

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    rail.addEventListener("scroll", onScroll, { passive: true });
    return () => rail.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, interacted]);

  return (
    <div className="relative mt-8 -mx-8 sm:mx-0" data-testid={testId}>
      <div className={`flex items-center mb-3 ${label ? "justify-between" : "justify-end"}`}>
        {label && <span className="eyebrow">{label}</span>}
        <div className="hidden sm:flex gap-2">
          <button
            aria-label="Previous image"
            onClick={() => go(active - 1)}
            data-testid={`${idPrefix}-prev`}
            className="w-10 h-10 rounded-full border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/15 transition-colors"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            aria-label="Next image"
            onClick={() => go(active + 1)}
            data-testid={`${idPrefix}-next`}
            className="w-10 h-10 rounded-full border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/15 transition-colors"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div ref={railRef} className="feat-rail" data-testid={`${idPrefix}-rail`}>
        {images.map((src, i) => (
          <div key={i} data-testid={`${idPrefix}-slot-${i + 1}`} className="feat-slide">
            <button
              type="button"
              onClick={() => openLightbox(i)}
              data-testid={`${idPrefix}-open-${i + 1}`}
              aria-label={`Open image ${i + 1} full screen`}
              className="w-full h-[56vh] sm:h-[64vh] rounded-xl border border-gold/40 bg-black/40 overflow-hidden flex items-center justify-center cursor-zoom-in"
            >
              <img
                src={src}
                alt={`${alt} ${i + 1}`}
                className="max-w-full max-h-full w-auto h-auto object-contain"
                draggable="false"
                loading="lazy"
              />
            </button>
          </div>
        ))}
      </div>

      {/* mobile swipe hint */}
      <div
        className={`sm:hidden flex justify-end mt-2 pr-1 text-gold text-xs tracking-[0.25em] uppercase transition-opacity duration-500 ${
          interacted ? "opacity-0" : "opacity-90"
        }`}
        data-testid={`${idPrefix}-swipe-hint`}
      >
        swipe →
      </div>

      {/* dot indicators + position */}
      <div className="flex items-center justify-center gap-3 mt-4">
        <div className="flex items-center gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to image ${i + 1}`}
              onClick={() => go(i)}
              data-testid={`${idPrefix}-dot-${i + 1}`}
              className={`rounded-full transition-all duration-300 ${
                active === i ? "w-5 h-2 bg-gold" : "w-2 h-2 bg-gold/35 hover:bg-gold/60"
              }`}
            />
          ))}
        </div>
        <span className="text-[11px] tracking-widest text-gold/70" data-testid={`${idPrefix}-position`}>
          {active + 1} OF {images.length}
        </span>
      </div>

      {/* Lightbox / image viewer — portaled to body so it's truly viewport-fixed
          and layered above the fixed 3D card stage */}
      {createPortal(
        <AnimatePresence>
          {lightbox !== null && (
            <motion.div
              className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
              style={{ touchAction: "pinch-zoom" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={closeLightbox}
              onTouchStart={(e) => {
                touchX.current = e.touches[0].clientX;
              }}
              onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                if (Math.abs(dx) > 45) lbGo(dx < 0 ? 1 : -1);
                touchX.current = null;
              }}
              data-testid={`${idPrefix}-lightbox`}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  closeLightbox();
                }}
                aria-label="Close viewer"
                data-testid={`${idPrefix}-lightbox-close`}
                className="absolute top-4 right-4 z-10 w-11 h-11 rounded-full border border-gold/50 text-gold flex items-center justify-center hover:bg-gold/15 transition-colors"
              >
                <X size={20} />
              </button>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  lbGo(-1);
                }}
                aria-label="Previous image"
                data-testid={`${idPrefix}-lightbox-prev`}
                disabled={lightbox === 0}
                className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-gold/50 text-gold items-center justify-center hover:bg-gold/15 transition-colors disabled:opacity-25"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  lbGo(1);
                }}
                aria-label="Next image"
                data-testid={`${idPrefix}-lightbox-next`}
                disabled={lightbox === images.length - 1}
                className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-gold/50 text-gold items-center justify-center hover:bg-gold/15 transition-colors disabled:opacity-25"
              >
                <ChevronRight size={24} />
              </button>

              <motion.img
                key={lightbox}
                src={images[lightbox]}
                alt={`${alt} ${lightbox + 1}`}
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                className="max-w-[94vw] max-h-[88vh] w-auto h-auto object-contain select-none"
                draggable="false"
              />

              <div
                className="absolute bottom-5 left-1/2 -translate-x-1/2 text-sm tracking-widest text-gold/80"
                data-testid={`${idPrefix}-lightbox-counter`}
              >
                {lightbox + 1} / {images.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
